<template>
  <div class="relative flex grow flex-col overflow-hidden">
    <div
      v-if="error"
      class="flex h-full flex-col items-center justify-center bg-red-100 px-2 py-1 text-center text-xs"
    >
      <div class="font-bold text-red-500">Error rendering preview</div>
      <div class="text-red-500">{{ error }}</div>
    </div>
    <!-- Force light color-scheme for email previews so browser defaults (blockquotes,
         etc.) don't inherit the admin's dark mode. For web/archive/portal modes, inherit
         so dark-mode-aware content previews correctly. -->
    <iframe
      v-else
      ref="frame"
      src="about:blank"
      :class="{
        'w-full grow': true,
        // Similar to the above comment: classic emails don't have a manual background set, so without this
        // the text appears black (correct!) on the admin dark mode background.
        'bg-white': mode === 'email',
      }"
      :style="{ colorScheme: mode === 'email' ? 'light' : 'inherit' }"
    />

    <div
      v-if="pending"
      class="absolute bottom-3 right-3 rounded bg-muted px-2 py-1 text-xs"
    >
      <PendingIndicator pending /> Rendering...
    </div>
  </div>
</template>

<script setup lang="ts">
import nanomorph from "nanomorph";
import {
  computed,
  inject,
  onWatcherCleanup,
  ref,
  useTemplateRef,
  watch,
} from "vue";

import PendingIndicator from "@/components/utilities/PendingIndicator.vue";
import type { RenderResult } from "@/lib/api/queries/emails/render";
import { ARCHIVE_HIGHLIGHT_KEY } from "@/pages/settings/archives/lib/archive-highlight";
import { useNewsletter } from "@/store/stores/account";
import type { PreviewContext } from "@/store/stores/ui-states";

import { usePreviewScrollSync } from "../composables/use-scroll-sync";
import archiveHighlighterUrl from "./scripts/archive-region-highlighter.ts?worker&url";
import highlighterUrl from "./scripts/element-highlighter.ts?worker&url";
import { patchNoMorphElements } from "./utils/morph";

// the URL provided by the import won't contain the origin in prod, which won't
// work because then it'd resolve against `blob:https://buttondown.com/<uuid>`
const actualHighlighterUrl = import.meta.env.PROD
  ? new URL(highlighterUrl, location.origin).href
  : highlighterUrl;

const actualArchiveHighlighterUrl = import.meta.env.PROD
  ? new URL(archiveHighlighterUrl, location.origin).href
  : archiveHighlighterUrl;

const props = defineProps<{
  pending?: boolean;
  error?: unknown;
  result: RenderResult | undefined;

  mode: PreviewContext;
  showClassHighlights?: boolean;
  scrollSyncEnabled?: boolean;
}>();

const newsletter = useNewsletter();
const archiveHighlight = inject(ARCHIVE_HIGHLIGHT_KEY, null);

const iframeRef = useTemplateRef("frame");
const { uid } = usePreviewScrollSync(
  iframeRef,
  computed(() => props.scrollSyncEnabled ?? false)
);

const isScrolled = ref(false);

watch(iframeRef, (iframe) => {
  if (!iframe) return;

  let detach: (() => void) | undefined;

  const attach = () => {
    detach?.();
    const cw = iframe.contentWindow;
    if (!cw) return;

    const handler = () => {
      const el = iframe.contentDocument?.scrollingElement;
      isScrolled.value = el ? el.scrollTop > 0 : false;
    };

    cw.addEventListener("scroll", handler, { passive: true });
    handler();
    detach = () => cw.removeEventListener("scroll", handler);
  };

  iframe.addEventListener("load", attach);
  attach();

  onWatcherCleanup(() => {
    detach?.();
    iframe.removeEventListener("load", attach);
  });
});

defineExpose({ isScrolled });

const replacedBody = computed(() => {
  const result = props.result;
  const mode = props.mode;

  let source = "";

  if (result !== undefined) {
    source = result[mode] ?? "";
  }

  // - Set links to open in a new tab by default
  // - This isn't an issue in production, but it is a little annoying that
  //   relative links (e.g. /mary-ext/archive/) doesn't work in dev, let's fix
  //   that by adding the correct origin.
  let headInjection = `<base target="_blank" href="${location.origin}">`;

  // Inject element highlighter script if enabled
  if (props.showClassHighlights) {
    headInjection += `<script type="module" src="${actualHighlighterUrl}"></${"script"}>`;
  }

  // Inject archive region highlighter when archive highlight context is active
  if (archiveHighlight) {
    headInjection += `<script type="module" src="${actualArchiveHighlighterUrl}"></${"script"}>`;
  }

  // Inject scroll sync script if enabled
  if (props.scrollSyncEnabled) {
    headInjection += `<script>window.parent.postMessage(${JSON.stringify(
      uid
    )}, "*");</${"script"}>`;
  }

  if (source === undefined) {
    return "";
  }

  source = source.replace(/(?=<\/head>)/, headInjection);

  if (
    mode === "web" ||
    mode === "archive" ||
    mode === "subscribe" ||
    mode === "portal"
  ) {
    // It's insane that we have to do this, and I'm sure there's a better way, but for now...
    source = source.replaceAll(
      'href="/static',
      'href="https://buttondown.com/static'
    );
  }

  // If we're previewing an email, and plaintext is flagged, `pre` it.
  if (props.mode === "email" && newsletter.value.only_send_plaintext_emails) {
    source = `<pre style="text-wrap: wrap;">${source}</pre>`;
  }

  return source;
});

// Archive highlight: relay hoveredFeature changes to iframe and listen for
// hover events from iframe
if (archiveHighlight) {
  const { hoveredFeature, enabledFeatures } = archiveHighlight;
  let fromIframe = false;

  watch(
    [hoveredFeature, enabledFeatures],
    ([featureId, enabled]) => {
      if (fromIframe) {
        fromIframe = false;
        return;
      }
      const iframe = iframeRef.value;
      iframe?.contentWindow?.postMessage(
        {
          type: "archive:highlight",
          featureId,
          enabledFeatures: enabled,
        },
        "*"
      );
    },
    { immediate: true }
  );

  const onMessage = (ev: MessageEvent) => {
    if (ev.data?.type === "archive:region-hover") {
      fromIframe = true;
      hoveredFeature.value = ev.data.featureId;
    } else if (ev.data?.type === "archive:region-unhover") {
      fromIframe = true;
      hoveredFeature.value = null;
    }
  };

  window.addEventListener("message", onMessage);
  watch(
    iframeRef,
    () => {
      onWatcherCleanup(() => window.removeEventListener("message", onMessage));
    },
    { immediate: true }
  );
}

watch(iframeRef, (iframe) => {
  if (import.meta.env.VITEST || !iframe) {
    return;
  }

  // Track previous mode to detect mode switches and force full reload
  let prevMode: PreviewContext | undefined;

  const cleanup = watch(
    replacedBody,
    (source, prevSource) => {
      let handled = false;

      // Detect if the mode changed - if so, force a full reload to reinitialize
      // third-party embeds (e.g. TikTok) that don't work well with DOM morphing
      const modeChanged = prevMode !== props.mode;
      prevMode = props.mode;

      const scriptCountChanged =
        prevSource?.match(/<script/g)?.length !==
        source.match(/<script/g)?.length;

      // Attempt to diff the document to get a more tear-free experience
      // Skip nanomorph if we're transitioning from empty result to real content
      // or if the mode changed (embeds need full reload)
      if (prevSource && !modeChanged && !scriptCountChanged) {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(source, "text/html");
          patchNoMorphElements(doc);
          nanomorph(iframe.contentDocument!, doc);

          // Notify any scripts that the DOM was morphed
          iframe.contentWindow?.postMessage("preview:morphed", "*");

          handled = true;
        } catch (err) {
          console.warn(`Failed to diff live preview:`, err);
        }
      }

      if (!handled) {
        const blob = new Blob([source], { type: "text/html" });
        const blobUrl = URL.createObjectURL(blob);
        onWatcherCleanup(() => URL.revokeObjectURL(blobUrl));

        iframe.contentWindow?.location.replace(blobUrl);
      }
    },
    { immediate: true }
  );

  onWatcherCleanup(cleanup);
});
</script>
